import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

import clsx from 'clsx';
import endOfDay from 'date-fns/endOfDay';
import { ReactElement } from 'react';
import { DateRange as ReactDateRange, RangeKeyDict } from 'react-date-range';
import { DateTimeRangeWithRelative } from 'src/WebApiController';

import { DateRange, DateRangePresets } from './DateRangePresets';
import { DateRangeRelativePresets } from './DateRangePresets/DateRangeRelativePresets';
import { DateRangeSelectorProps } from './DateRangeSelector';
import * as styles from './DateRangeSelector.css';

type DateSelectorsProps = DateRangeSelectorProps & {
  // Exact selector props
  onSuggestionClick: (range: DateRange) => void;
  dateValue: { start: string; end: string };

  // Trailing selector props
  onSuggestionRelativeClick: (range: DateTimeRangeWithRelative) => void;
  dateRange: DateTimeRangeWithRelative | null;

  // Custom selector props
  onPreviewChange?: (previewDate?: Date) => void;
  getFormattedDate: (time: string | null | undefined) => Date;
  defaultFromlabel: string;
  defaultToLabel: string;
  fromZonedTime: (date: Date) => Date;
  onDateRangePickerChange: (
    from: Date | undefined,
    to: Date | undefined
  ) => void;
};

export const ExactDateSelector = ({
  disabled,
  onSuggestionClick,
  dateValue,
  useSiteTimeZone,
  exactPresetNames = [],
  ...presetProps
}: DateSelectorsProps): ReactElement => (
  <div className={styles.dateRangePresetsContainer}>
    <DateRangePresets
      {...presetProps}
      presetNames={exactPresetNames}
      disabled={disabled}
      onClick={onSuggestionClick}
      selectedDateRange={dateValue}
      useSiteTimeZone={useSiteTimeZone}
      hideSuggestionTitle
      variant={'v2'}
    />
  </div>
);

export const RelativeDateSelector = ({
  disabled,
  onSuggestionRelativeClick,
  dateRange,
  useSiteTimeZone,
  trailingPresetNames = [],
  ...presetProps
}: DateSelectorsProps): ReactElement => (
  <div className={styles.dateRangePresetsContainer}>
    <DateRangeRelativePresets
      {...presetProps}
      presetNames={trailingPresetNames}
      disabled={disabled}
      onClick={onSuggestionRelativeClick}
      selectedDateRange={dateRange as DateTimeRangeWithRelative}
      useSiteTimeZone={useSiteTimeZone}
      hideSuggestionTitle
      variant="v2"
    />
  </div>
);

export const CustomDateSelector = ({
  onPreviewChange,
  className,
  dateDisplayFormat,
  retainEndDateOnFirstSelection,
  isEndDateFixed,
  getFormattedDate,
  dateRange,
  startDatePlaceholder,
  defaultFromlabel,
  endDatePlaceholder,
  defaultToLabel,
  rangeColors,
  locale,
  useSiteTimeZone,
  fromZonedTime,
  onDateRangePickerChange,
}: Omit<
  DateSelectorsProps,
  'disabled' | 'onSuggestionClick' | 'dateValue' | 'onSuggestionRelativeClick'
>): ReactElement => {
  const handleCustomDateChange = (range: RangeKeyDict) => {
    const startDate =
      range.range.startDate && useSiteTimeZone
        ? fromZonedTime(range.range.startDate)
        : range.range.startDate;
    const rangeSelectorEndDate = range.range.endDate
      ? useSiteTimeZone
        ? fromZonedTime(endOfDay(range.range.endDate))
        : endOfDay(range.range.endDate)
      : undefined;

    const endDate = isEndDateFixed
      ? dateRange?.end
        ? new Date(dateRange.end)
        : new Date()
      : rangeSelectorEndDate;

    onDateRangePickerChange(startDate, endDate);
  };

  return (
    <div className={styles.customDateRangeContainer}>
      <ReactDateRange
        key="custom-date-range"
        showDateDisplay={false}
        onPreviewChange={onPreviewChange}
        editableDateInputs={true}
        className={clsx(className, styles.dateRangePicker)}
        dateDisplayFormat={dateDisplayFormat}
        retainEndDateOnFirstSelection={retainEndDateOnFirstSelection}
        maxDate={isEndDateFixed ? getFormattedDate(dateRange?.end) : undefined}
        ranges={[
          {
            startDate: getFormattedDate(dateRange?.start),
            endDate: getFormattedDate(dateRange?.end),
            key: 'range',
          },
        ]}
        startDatePlaceholder={startDatePlaceholder ?? defaultFromlabel}
        endDatePlaceholder={endDatePlaceholder ?? defaultToLabel}
        onChange={handleCustomDateChange}
        rangeColors={rangeColors}
        locale={locale}
      />
    </div>
  );
};

export const dateSelectors = {
  Exact: ExactDateSelector,
  Relative: RelativeDateSelector,
  Custom: CustomDateSelector,
} as const;

export type DateSelectorType = keyof typeof dateSelectors;
